import { AitHeaderCellData, AitHeaderGroupData, AitLocationHeaderCell } from "./interface";
import { trGetCellFromLocation } from "./trGetCellFromLocation";

export interface trCanAddRowSpanReturn<T> {
  canAdd: true;
  reasonNot: undefined;
  targetCell: AitHeaderCellData<T>;
  hideCell: AitHeaderCellData<T>;
}

export interface trCanNotAddRowSpanReturn<T> {
  canAdd: false;
  reasonNot: string;
  targetCell: AitHeaderCellData<T> | undefined;
  hideCell: AitHeaderCellData<T> | undefined;
}

/**
 * Determine whether a row span can be added at the given header location.
 *
 * @template T - The header cell payload type
 * @param headerData - Header group data containing header rows and cells
 * @param location - Location describing the target header cell (row/column/originalColumn/isRepeat etc.)
 * @param newRowSpan - Desired number of rows to span for the target cell
 * @returns Either a success object (`canAdd: true`) containing the `targetCell`
 *          that will be expanded and the `hideCell` that will be hidden, or a
 *          failure object (`canAdd: false`) containing a `reasonNot` string.
 */
export const trCanAddRowSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newRowSpan: number,
): trCanAddRowSpanReturn<T> | trCanNotAddRowSpanReturn<T> => {
  let reasonNot = "";
  let hideCell: AitHeaderCellData<T> | undefined;

  // Check target cell exists
  const targetCell = trGetCellFromLocation(
    {
      headerData: headerData,
      bodyData: [],
      coreTrafficLight: "BlankNotEvaluated",
      columnInformation: [],
    },
    location,
    true,
  ) as AitHeaderCellData<T> | undefined;
  if (targetCell === undefined) {
    reasonNot = "AddRowSpan: target cell not found";
  } else if (targetCell.rowSpan === newRowSpan) {
    reasonNot = "";
  } else if (location.isRepeat) {
    reasonNot = "AddRowSpan: this is a repeat cell";
  } else if (targetCell.colSpan !== 1) {
    reasonNot = "AddRowSpan: cell already has column span";
  } else {
    // Get the cell to hide
    if (headerData.rows.length <= location.row + newRowSpan - 1) {
      reasonNot = "AddRowSpan: header does not have enough rows to expand";
    } else {
      hideCell = headerData.rows[location.row + newRowSpan - 1]!.cells[location.originalColumn];
      if (hideCell.colSpan !== 1 || hideCell.rowSpan !== 1) {
        reasonNot = "AddRowSpan: hide cell is not suitable for hiding";
      }
    }
  }

  return reasonNot !== ""
    ? { canAdd: false, reasonNot, targetCell, hideCell }
    : {
        canAdd: true,
        reasonNot: undefined,
        targetCell: targetCell!,
        hideCell: hideCell!,
      };
};

/**
 * Apply a row span change to header data.
 *
 * This will set the `rowSpan` on the target cell and set the corresponding
 * hide cell's `rowSpan` to 0. The function throws when the change is not
 * permitted (use `trCanAddRowSpan` to check beforehand).
 *
 * @template T - The header cell payload type
 * @param headerData - The header group data to be updated
 * @param location - Location describing the header cell to update
 * @param newSpan - The new rowSpan value to apply
 * @returns An updated `AitHeaderGroupData<T>` instance with the row span
 *          changes applied.
 * @throws Error when the span cannot be added (reason returned by `trCanAddRowSpan`)
 */
export const trAddRowSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newSpan: number,
) => {
  // Check ok to add row span
  const { canAdd, reasonNot, targetCell, hideCell } = trCanAddRowSpan(
    headerData,
    location,
    newSpan,
  );

  // Already updated
  if (targetCell?.rowSpan === newSpan) return;

  // Cannot add
  if (!canAdd) throw new Error(reasonNot);

  // Create return object
  const newHeaderData = { ...headerData };
  newHeaderData.rows = headerData.rows.map((row) => {
    if (row.aitid !== location.rowId && !row.cells.find((c) => c.aitid === hideCell.aitid))
      return row;
    const newCells = row.cells.map((cell) => {
      if (cell.aitid === targetCell.aitid) {
        return {
          ...cell,
          rowSpan: newSpan,
        };
      }
      if (cell.aitid === hideCell.aitid) {
        return {
          ...cell,
          rowSpan: 0,
        };
      }
      return cell;
    });
    return { ...row, cells: newCells };
  });
  return newHeaderData;
};
